import { Box, Button, Modal } from '@amzn/awsui-components-react';
import { useTranslation } from 'react-i18next';
import { EventInput, Event, ShiftInput } from '@amzn/red-velvet-api';
import { useUser } from '../../../../hooks/user';
import { EventPreviewHeader } from './EventPreviewHeader';
import { EventDetailsSection } from '../../../better/fullEventDisplay';
import { ShiftPreviewTable } from './ShiftPreviewTable';

export interface EventPreviewModalProps {
  eventId: string;
  currentEventFields: EventInput;
  currentShiftFields: ShiftInput[];
  isVisible: boolean;
  onDismiss: () => void;
}

export function EventPreviewModal({
  eventId,
  currentEventFields,
  currentShiftFields,
  isVisible,
  onDismiss,
}: EventPreviewModalProps) {
  const namespaces = ['events', 'translation'];
  const { t } = useTranslation(namespaces);
  const user = useUser();

  const earliest = currentShiftFields.reduce<Date | undefined>(
    (min, s) => (!min || (s.startDateTime && s.startDateTime < min) ? s.startDateTime : min),
    undefined
  );
  const latest = currentShiftFields.reduce<Date | undefined>((max, s) => {
    const end = s.endDateTime ?? s.startDateTime;
    return !max || (end && end > max) ? end : max;
  }, undefined);

  const previewEvent: Event = {
    ...currentEventFields,
    eventId,
    title: currentEventFields.title ?? '',
    description: currentEventFields.description ?? '',
    shortDescription: currentEventFields.shortDescription ?? '',
    heroImageUri: currentEventFields.heroImageUrl,
    startTimestamp: earliest ?? new Date(),
    endTimestamp: latest,
    source: 'VC',
    rvSignupSupported: false,
    location: {
      ...currentEventFields.location,
    },
    contacts: [
      ...(currentEventFields?.amazonContacts ?? []),
      ...(currentEventFields?.externalContacts ?? []),
    ],
    vcCreated: true,
  };

  const eventData = {
    event: previewEvent,
    // Shifts can be empty since EventDetailsSection does not need Shifts data
    shifts: [],
    signups: [],
    user,
  };

  return (
    <Modal
      onDismiss={onDismiss}
      visible={isVisible}
      header={t('previewModal.title')}
      size="max"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>
            {t('previewModal.close')}
          </Button>
        </Box>
      }
    >
      <div className="full-event">
        <EventPreviewHeader
          currentEventFields={currentEventFields}
          currentShiftFields={currentShiftFields}
        />
        <EventDetailsSection {...eventData} />
        <ShiftPreviewTable
          shifts={currentShiftFields}
          eventLocationType={currentEventFields.location?.locationType}
        />
      </div>
    </Modal>
  );
}
